import { Group, Stack, Text } from "@mantine/core"
import { Badge, Card, Missing, CountryCell } from "@tfg/ui"
import { useTranslation } from "react-i18next"

import { EnrichedOperationDto } from "~/gen/api"
import {
  getSupplierCardHeight,
} from "~/pages/Brand/Products/Details/constants.ts"
import { COUNTRIES } from "~/utils/countries"
import { useGetOperations } from "~/utils/operations"

import type { ForkOriginCue } from "./traceability-graph.view-model"

const SupplierCardDetails = ({
  organizationName,
  address,
}: {
  organizationName: string | undefined
  address: string | undefined
}) => {
  const { t } = useTranslation("products")

  return (
    <>
      <Group align="flex-start" justify="space-between" wrap="nowrap">
        <Text>{t("traceability.supplierCard.company")}:</Text>
        <Text fw={700} style={{ textAlign: "right" }}>
          {organizationName ?? <Missing />}
        </Text>
      </Group>
      <Group align="flex-start" justify="space-between" wrap="nowrap">
        <Text>{t("traceability.supplierCard.address")}:</Text>
        <Text style={{ textAlign: "right" }} fw={700}>
          {address || <Missing />}
        </Text>
      </Group>
    </>
  )
}

type SupplierCardProps = {
  activityName: string
  itemReference?: string
  parentReferences: string[]
  operation?: EnrichedOperationDto
  originCue?: ForkOriginCue
}

export const SupplierCard = ({
  activityName,
  itemReference,
  parentReferences,
  operation,
  originCue,
}: SupplierCardProps) => {
  const { t } = useTranslation("products")
  const { t: tTraceability } = useTranslation("traceability")

  const operationInfo = useGetOperations()[activityName]
  const references = [...parentReferences, itemReference].filter(
    (reference): reference is string => Boolean(reference)
  )
  const uniqueReferences = Array.from(new Set(references))
  const labelledReferences = uniqueReferences
    .slice(0, 3)
    .map((reference, index) => {
      if (index === 0) {
        return {
          label: t("traceability.supplierCard.productReference"),
          reference,
        }
      }

      if (index === 1) {
        return {
          label: t("traceability.supplierCard.componentReference"),
          reference,
        }
      }

      return {
        label: t("traceability.supplierCard.materialReference"),
        reference,
      }
    })

  return (
    <Card w={340} h={getSupplierCardHeight(originCue !== undefined)}>
      <Stack gap="xs">
        {originCue !== undefined && (
          <>
            <Badge
              size="xs"
              p="compact-xs"
              fz="xxs"
              h="1.25rem"
              variant="info"
              text={tTraceability("sourceContext.badge")}
            />
            <Text size="xs" c="dimmed">
              {tTraceability("sourceContext.originCue", originCue)}
            </Text>
          </>
        )}
        <Group justify="space-between" mb="xs" wrap="nowrap" align="flex-end">
          <Group gap="xs" wrap="nowrap">
            {operationInfo.icon && <operationInfo.icon />}
            <Text style={{ fontWeight: "bold" }}>{operationInfo.name}</Text>
          </Group>
          <CountryCell
            countryIso3166={operation?.country.value}
            countriesMapper={COUNTRIES}
          />
        </Group>
        {labelledReferences.length > 0 ? (
          labelledReferences.map(({ label, reference }) => (
            <Group
              key={`${label}-${reference}`}
              align="flex-start"
              justify="space-between"
              wrap="nowrap"
            >
              <Text>{label}:</Text>
              <Text style={{ textAlign: "right" }} truncate fw={700}>
                {reference}
              </Text>
            </Group>
          ))
        ) : (
          <Group align="flex-start" justify="space-between" wrap="nowrap">
            <Text>{t("traceability.supplierCard.productReference")}:</Text>
            <Text style={{ textAlign: "right" }} truncate fw={700}>
              <Missing />
            </Text>
          </Group>
        )}

        <SupplierCardDetails
          organizationName={operation?.productionSite.value?.organizationName}
          address={operation?.productionSite.value?.address}
        />

        <Text />
      </Stack>
    </Card>
  )
}
